import { type } from 'arktype';

/**
 * Body of `POST /time-tracking/rule-instances/parse` (story 34-4): free text
 * (GAV paragraph, staff reglement, plain-language sentence) to be mapped onto
 * known rule kinds.
 */
export const ruleParseRequestSchema = type({
  text: '0 < string <= 10000',
  // Kind context of the adjust screen (34-2): enables partial recognition —
  // "Pause soll 45 Minuten sein" resolves against the kind being adjusted.
  'kindKey?': 'string > 0',
});
export type RuleParseRequest = typeof ruleParseRequestSchema.t;

/**
 * A sentence that maps onto a registered rule kind. GOVERNANCE (Konzept 05
 * §7.2, hard): this is ONLY a suggestion — the UI routes every mappable
 * suggestion through the validate + dry-run gates of the adjust screen;
 * direct creation from parser output is forbidden.
 */
export interface RuleParseMappableSuggestion {
  mappable: true;
  kindKey: string;
  params: Record<string, unknown>;
  /** 0–100; deterministic parser reports a fixed heuristic confidence. */
  confidencePct: number;
  sourceSentence: string;
  /**
   * True when only SOME params of the kind were recognized (kind-context
   * parsing on the adjust screen) — the client merges them into the current
   * form values; the merged set still runs through validate + dry run.
   */
  partial?: boolean;
}

/** Structured developer signal for a sentence NO registered kind can express. */
export interface RuleParseRequirement {
  title: string;
  sourceSentence: string;
  /**
   * What the new kind must be able to do. Capability codes (`code` or
   * `code:value`, e.g. `count-sequence`, `threshold:5`) — the UI translates
   * known codes and falls back to the raw string.
   */
  capabilities: string[];
}

export interface RuleParseUnmappableSuggestion {
  mappable: false;
  requirement: RuleParseRequirement;
}

export type RuleParseSuggestion = RuleParseMappableSuggestion | RuleParseUnmappableSuggestion;

export interface RuleParseResult {
  suggestions: RuleParseSuggestion[];
  /** Strategy that produced the result: 'ai' (LLM, only when configured) or 'deterministic' (regex fallback). */
  parser: 'ai' | 'deterministic';
}

/**
 * MIME allowlist of `POST /time-tracking/rule-instances/parse-document`
 * (mockup 16 v2 + document upload): shared between the multer file filter
 * (API) and the file-picker `accept` attribute (UI).
 */
export const RULE_PARSE_DOCUMENT_MIMES = Object.freeze([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
] as const);

/** Extracted document text may exceed what the parser handles — hard cap, sentence-truncated. */
export const RULE_PARSE_DOCUMENT_MAX_TEXT_CHARS = 50000;

/** `parse-document` upload cap — enforced by the server multer limit, pre-checked by the assistant. */
export const RULE_PARSE_DOCUMENT_MAX_BYTES = 10_485_760;

/**
 * Result of the document upload path: the parse result PLUS the extracted
 * plain text — the assistant UI renders it with the recognized sentences
 * highlighted, exactly like the pasted-text path.
 */
export interface RuleParseDocumentResult extends RuleParseResult {
  /** Extracted (possibly truncated) plain text the suggestions refer to. */
  text: string;
  /** True when the extracted text exceeded the cap and was cut off. */
  truncated: boolean;
}
